import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CircleNotch, ShieldSlash, Info } from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';
import { toast } from 'sonner';
import {
  PlatformFieldRow,
  PlatformPanel,
  PlatformSectionIntro,
} from '@/components/platform/PlatformSettingsPrimitives';

/**
 * Security — how the operator signs in.
 *
 * Kept APART from My Account, the same separation the chambers side draws. A password is not a
 * profile field: it is entered differently (current password, twice-typed replacement), it ends
 * every other session when it changes, and mixing it into a blur-to-save profile form would put
 * a credential control next to a display name.
 *
 * This page previously also carried the profile CARD — name and email rendered as static text.
 * That is identity, and it now lives on My Account (`SettingsAccountPage.tsx`) where the name can
 * actually be edited. What stays here is the SIGN-IN RECORD: when the password last changed, and
 * when and from where the account was last used. Those are security facts, they are the ones an
 * operator checks when something looks wrong, and they sit next to the control that answers them.
 */

interface SecurityRecord {
  status: string;
  lastLoginAt?: string;
  lastLoginIp?: string;
  passwordChangedAt?: string;
}

function formatDateTime(str?: string) {
  if (!str) return null;
  return new Date(str).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Mirror of PLATFORM_MIN_PASSWORD_LENGTH in
// apps/api/src/modules/platform-auth/platform-auth.schema.ts.
// The API cannot import from @jurivoxa/shared (rootDir constraint), so this is a
// deliberate mirror rather than a shared import. Keep the two in step.
const PLATFORM_MIN_PASSWORD_LENGTH = 16;

export default function PlatformSettingsPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Same query key as My Account — one read of the operator's own record, shared by both
  // sections, so opening Security after My Account costs no second request.
  const { data } = useQuery<{ admin: SecurityRecord }>({
    queryKey: ['platform', 'auth', 'me'],
    queryFn: async () => {
      const res = await platformApi.get('/auth/me');
      return res.data?.data ?? res.data;
    },
  });
  const record = data?.admin;

  const passwordMutation = useMutation({
    mutationFn: async () => {
      await platformApi.post('/auth/change-password', { currentPassword, newPassword });
    },
    onSuccess: () => {
      toast.success('Password changed. All other sessions have been signed out.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    },
    onError: (err) => {
      toast.error(getPlatformApiErrorMessage(err, 'Failed to change password'));
    },
  });

  function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPasswordError('');
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    // Mirrors PLATFORM_MIN_PASSWORD_LENGTH in platform-auth.schema.ts. This check is a
    // COURTESY so the user gets a clear message instead of an opaque server rejection —
    // the server is the actual control. It previously said 8 while the server enforced 12,
    // so a 9-character password passed here and then failed server-side with no useful
    // explanation.
    if (newPassword.length < PLATFORM_MIN_PASSWORD_LENGTH) {
      setPasswordError(
        `New password must be at least ${PLATFORM_MIN_PASSWORD_LENGTH} characters. ` +
        'This account governs every chambers and has no second factor, so its length is ' +
        'what protects it — use a generated password from a manager.',
      );
      return;
    }
    passwordMutation.mutate();
  }

  const inputClass =
    'h-9 w-full rounded-lg border border-plat-border bg-plat-bg px-3 text-sm text-plat-fg placeholder-plat-faint focus:border-plat-focus focus:outline-none focus:ring-2 focus:ring-plat-focus/50';

  return (
    <div className="space-y-5">
      <PlatformSectionIntro
        title="Security"
        description="How you sign in to the Platform Console, and what protects the account"
      />

      <PlatformPanel
        title="Change password"
        description="Changing your password signs out every other active session immediately"
      >
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          {passwordError && (
            <div className="rounded-lg border border-plat-danger-line bg-plat-danger-soft px-4 py-3 text-sm text-plat-danger-text">
              {passwordError}
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary" htmlFor="current-password">
              Current password
            </label>
            <input
              id="current-password"
              type="password"
              required
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary" htmlFor="new-password">
              New password
            </label>
            <input
              id="new-password"
              type="password"
              required
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={inputClass}
              // Was "Min. 8 characters" while both this page and the server enforce 16 — a
              // placeholder that states a floor must state the real one.
              placeholder={`Min. ${PLATFORM_MIN_PASSWORD_LENGTH} characters`}
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-plat-secondary" htmlFor="confirm-password">
              Confirm new password
            </label>
            <input
              id="confirm-password"
              type="password"
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputClass}
            />
          </div>
          <button
            type="submit"
            disabled={passwordMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-plat-accent-solid px-4 py-2.5 text-sm font-medium text-plat-on-accent hover:bg-plat-accent-solid-hover disabled:cursor-not-allowed disabled:bg-plat-neutral-soft disabled:text-plat-disabled"
          >
            {passwordMutation.isPending && <CircleNotch className="h-4 w-4 animate-spin" />}
            Change password
          </button>
          <p className="text-xs text-plat-faint">
            Changing your password will sign out all other active sessions.
          </p>
        </form>
      </PlatformPanel>

      <PlatformPanel
        title="Sign-in record"
        description="Written by the platform. Nothing here is editable — that is what makes it evidence"
      >
        <PlatformFieldRow
          label="Password last changed"
          readOnly
          readOnlyValue={formatDateTime(record?.passwordChangedAt) ?? 'Never changed since the account was created'}
          readOnlyNote="Updated when you change it above, or when it is reset through the recovery flow."
        />
        <PlatformFieldRow
          label="Last sign-in"
          readOnly
          readOnlyValue={formatDateTime(record?.lastLoginAt) ?? 'No sign-in recorded'}
          readOnlyNote="If you do not recognise this, change your password now — doing so ends every other session."
        />
        <PlatformFieldRow
          label="Last sign-in from"
          readOnly
          readOnlyValue={record?.lastLoginIp ? <span className="text-xs">{record.lastLoginIp}</span> : '—'}
          readOnlyNote="The address the last successful sign-in came from."
        />

        {/* Said plainly rather than left as a missing feature the operator has to infer. The
            console keeps refresh tokens per session (`PlatformRefreshToken`) but exposes no way
            to list or revoke them individually; changing the password is the only lever, and it
            revokes all of them. Claiming otherwise, or showing an empty session list, would
            misrepresent what an operator can actually do about a stolen session. */}
        <div className="flex items-start gap-2 pt-3.5 text-xs text-plat-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-plat-info-text" />
          <span>
            There is no per-session list for platform accounts. To end every other session —
            including one you do not recognise — change your password above; that revokes them
            all immediately.
          </span>
        </div>
      </PlatformPanel>

      {/* 2FA status.
          Deliberately does NOT branch on admin.twoFaEnabled. The platform sign-in
          (platform-auth.service.ts) never checks that column, so rendering "your account is
          protected with two-factor authentication" would be a false claim about the most
          privileged account in the system — the one that can see every chambers. Tenant
          sign-in DOES enforce 2FA (auth.service.ts), which is the asymmetry to close.
          When enforcement lands in platform-auth, restore the enabled/disabled branch. */}
      <PlatformPanel title="Two-factor authentication" description="A second factor at sign-in">
        <div className="flex items-center gap-3">
          <ShieldSlash className="h-6 w-6 text-plat-warning-text" weight="fill" />
          <div>
            <div className="text-sm font-medium text-plat-secondary">Not available for platform accounts</div>
            <div className="text-xs text-plat-muted">
              Platform Console sign-in is password-only. Two-factor authentication is enforced for
              chambers accounts, but not yet for this one — use a long, unique password until it is.
            </div>
          </div>
          <span className="ml-auto inline-flex items-center rounded-md bg-plat-warning-soft px-2 py-0.5 text-xs font-medium text-plat-warning-text">
            Password only
          </span>
        </div>
      </PlatformPanel>
    </div>
  );
}
